import React, { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import HubPageShell from "../components/layout/HubPageShell";
import ProductionSupportChatPanel from "../components/production-support/ProductionSupportChatPanel";
import { useProductionSupportAgentChat } from "../hooks/useProductionSupportAgentChat";
import { useOperationalErrors } from "../hooks/useOperationalErrors";
import { useBookmarkableTab } from "../hooks/useBookmarkableTab";
import { useIsCsr } from "../hooks/useIsCsr";
import { useAuth } from "../contexts/AuthContext";
import { isProductManager, isProductionSupportAdmin } from "../utils/appRoles";
import { apiService } from "../api/services/apiService";
import { HUB_CHAT_PANEL_HEIGHT } from "../utils/hubLayout";
import {
  buildGuideAnalyzeMessage,
  readGuideFile,
  GUIDE_FILE_ACCEPT,
  type GuideFileReadResult,
} from "../utils/guideFileReader";
import {
  parseOperationalErrorExtractions,
  type OperationalErrorExtractionRow,
} from "../utils/productionSupportOutput";
import type { Document, OperationalError } from "../types";
import { EntityStatus, GuideSourceFormat } from "../types";
import { getTenantId } from "../utils/jwtUtils";
import { PM_HUBS } from "../utils/pmHubRoutes";
import {
  OPERATOR_DOCUMENT_ENTITY_CLASSES,
  OPERATOR_DOCUMENT_CLASS_LABELS,
  isOperatorCatalogDocument,
  type OperatorDocumentEntityClass,
} from "../utils/operatorDocuments";

const TAB_SLUGS = ["ask", "catalog", "guides", "shortcuts"] as const;

const QUICK_PROMPTS = [
  "Goal: explain TOKEN_EXPIRED and the restore steps for the partner JWT API.",
  "Goal: identify why checkout API returns 502 and the first restore steps.",
];

const ProductionSupportOperatorHubPage: React.FC = () => {
  const { user } = useAuth();
  const isPm = isProductManager(user?.roles ?? []);
  const canAuthorGuides =
    isPm || isProductionSupportAdmin(user?.roles ?? []);
  const isCsrUser = useIsCsr();
  const [, setSearchParams] = useSearchParams();
  const { tabIndex, setTabIndex } = useBookmarkableTab({ slugs: TAB_SLUGS });

  const goToTabSlug = (slug: (typeof TAB_SLUGS)[number]) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", slug);
        return next;
      },
      { replace: true },
    );
  };
  const chat = useProductionSupportAgentChat();
  const { errors: catalog, loading: catalogLoading, error: catalogError, refresh: refreshCatalog } =
    useOperationalErrors();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<OperationalError[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [selectedError, setSelectedError] = useState<OperationalError | null>(null);

  const [guideRead, setGuideRead] = useState<GuideFileReadResult | null>(null);
  const [guideReadError, setGuideReadError] = useState<string | null>(null);
  const [guideLoading, setGuideLoading] = useState(false);
  const [extractions, setExtractions] = useState<OperationalErrorExtractionRow[]>([]);
  const [selectedRows, setSelectedRows] = useState<Record<number, boolean>>({});
  const [applyLoading, setApplyLoading] = useState(false);
  const [savedGuides, setSavedGuides] = useState<Document[]>([]);
  const [guidesLoading, setGuidesLoading] = useState(false);
  const [guideEntityClass, setGuideEntityClass] =
    useState<OperatorDocumentEntityClass>("sop");

  const displayCatalog = searchResults ?? catalog;

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const results = await apiService.searchOperationalErrors(searchQuery.trim());
      setSearchResults(results);
    } catch (err: unknown) {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const loadSavedGuides = useCallback(async () => {
    setGuidesLoading(true);
    try {
      const docs = await apiService.getDocuments();
      setSavedGuides(docs.filter(isOperatorCatalogDocument));
    } finally {
      setGuidesLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (tabIndex === 2) loadSavedGuides();
  }, [tabIndex, loadSavedGuides]);

  const onFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setGuideReadError(null);
    setGuideLoading(true);
    setExtractions([]);
    try {
      const result = await readGuideFile(file);
      setGuideRead(result);
    } catch (err: unknown) {
      setGuideRead(null);
      setGuideReadError(err instanceof Error ? err.message : "Failed to read file");
    } finally {
      setGuideLoading(false);
    }
  };

  const analyzeGuide = async () => {
    if (!guideRead) return;
    const msg = buildGuideAnalyzeMessage(guideRead);
    await chat.sendMessage(msg);
    goToTabSlug("ask");
  };

  React.useEffect(() => {
    const lastAssistant = [...chat.messages].reverse().find((m) => m.role === "assistant");
    if (lastAssistant) {
      const rows = parseOperationalErrorExtractions(lastAssistant.content);
      if (rows.length > 0) setExtractions(rows);
    }
  }, [chat.messages]);

  const saveGuideToLibrary = async () => {
    const tenantId = getTenantId();
    if (!tenantId || !guideRead) return;
    await apiService.createDocument({
      tenantId,
      name: guideRead.fileName,
      status: EntityStatus.ACTIVE,
      tags: ["sop", "error-guide"],
      entityClass: guideEntityClass,
      content: guideRead.text,
      sourceFormat: guideRead.sourceFormat,
      sourceFileName: guideRead.fileName,
    });
    await loadSavedGuides();
  };

  const applyExtractions = async () => {
    const tenantId = getTenantId();
    if (!tenantId || !canAuthorGuides) return;
    const inputs = extractions
      .map((row, idx) => ({ row, idx }))
      .filter(({ idx, row }) => selectedRows[idx] && row.matchStatus !== "DUPLICATE")
      .map(({ row }) => ({
        tenantId,
        status: EntityStatus.ACTIVE,
        name: row.proposed.code ?? row.proposed.message ?? "Operational error",
        code: row.proposed.code ?? undefined,
        httpStatus: row.proposed.httpStatus ?? undefined,
        category: row.proposed.category as never,
        message: row.proposed.message ?? undefined,
        description: row.proposed.description ?? undefined,
        remediation: row.proposed.remediation ?? undefined,
        errorStrings: row.proposed.errorStrings ?? [],
      }));
    if (inputs.length === 0) return;
    setApplyLoading(true);
    try {
      await apiService.bulkCreateOperationalErrors(inputs);
      await refreshCatalog();
      setExtractions([]);
      setSelectedRows({});
      goToTabSlug("catalog");
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to apply extractions");
    } finally {
      setApplyLoading(false);
    }
  };

  const openSavedGuide = (doc: Document) => {
    setGuideRead({
      text: doc.content ?? "",
      fileName: doc.sourceFileName ?? doc.name,
      mimeType: "text/plain",
      sourceFormat: doc.sourceFormat ?? GuideSourceFormat.TEXT,
      truncated: false,
      title: doc.name ?? null,
    });
    setGuideReadError(null);
  };

  if (!isCsrUser && !isPm) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="warning">
          Production Support Operator Hub requires CSR / prod-support or product-manager access.
        </Alert>
      </Container>
    );
  }

  return (
    <HubPageShell
      pageTitle="Production Support Operator Hub"
      pageDescription="State a goal on Ask for catalog-backed triage. Guides & SOPs import catalog documents — for prod-support admins and product managers."
    >
      <Button
        component={RouterLink}
        to="/agent-dashboard"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, textTransform: "none" }}
      >
        Agents
      </Button>

      <Tabs value={tabIndex} onChange={setTabIndex} sx={{ mb: 2 }}>
        <Tab label="Ask" />
        <Tab label="Errors & messages" />
        <Tab label="Guides & SOPs" />
        <Tab label="Shortcuts" />
      </Tabs>

      {tabIndex === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <ProductionSupportChatPanel
              messages={chat.messages}
              onSend={chat.sendMessage}
              loading={chat.loading}
              error={chat.error}
              contextUsage={chat.contextUsage}
              quickPrompts={QUICK_PROMPTS}
              height={HUB_CHAT_PANEL_HEIGHT}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 2, position: "sticky", top: 24 }}>
              <Typography variant="subtitle2" gutterBottom>
                How to triage
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Write the outcome first, then optionally attach logs, screenshots, or vendor PDFs.
                Files are converted to Markdown before the agent runs. Ask attachments are not saved
                as catalog documents — use Guides & SOPs to publish SOP, runbook, or help-guide
                entries.
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Error lookup
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search codes, messages…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
                <Button variant="contained" size="small" onClick={handleSearch} disabled={searching}>
                  {searching ? "…" : "Go"}
                </Button>
              </Stack>
              {catalogError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {catalogError}
                </Alert>
              )}
              {catalogLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Stack spacing={1} sx={{ maxHeight: "min(55vh, 480px)", overflow: "auto" }}>
                  {displayCatalog.slice(0, 8).map((err) => (
                    <Paper
                      key={err.uuid}
                      variant="outlined"
                      sx={{ p: 1.5, cursor: "pointer" }}
                      onClick={() => setSelectedError(err)}
                    >
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {err.code ?? err.name}
                      </Typography>
                      {err.message && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {err.message}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                  {displayCatalog.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No errors found.
                    </Typography>
                  )}
                  {displayCatalog.length > 8 && (
                    <Button size="small" onClick={() => goToTabSlug("catalog")}>
                      View all in Errors tab
                    </Button>
                  )}
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabIndex === 1 && (
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              fullWidth
              size="small"
              label="Search codes, messages, strings"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button variant="contained" onClick={handleSearch} disabled={searching}>
              {searching ? "Searching…" : "Search"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchResults(null);
                setSearchQuery("");
              }}
            >
              Clear
            </Button>
          </Stack>
          {catalogError && <Alert severity="error">{catalogError}</Alert>}
          {catalogLoading ? (
            <CircularProgress />
          ) : (
            <Grid container spacing={2}>
              {displayCatalog.map((err) => (
                <Grid item xs={12} sm={6} key={err.uuid}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, cursor: "pointer" }}
                    onClick={() => setSelectedError(err)}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      {err.code ?? err.name}
                    </Typography>
                    {err.message && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {err.message}
                      </Typography>
                    )}
                    {err.category && (
                      <Chip size="small" label={err.category} sx={{ mt: 1, mr: 0.5 }} />
                    )}
                    {err.httpStatus != null && (
                      <Chip size="small" label={`HTTP ${err.httpStatus}`} sx={{ mt: 1 }} />
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      )}

      {tabIndex === 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Upload SOP / error guide
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Catalog import for prod-support admins and product managers. Incident files belong on Ask.
              </Typography>
              <Button
                component={RouterLink}
                to={PM_HUBS.knowledgeSearch}
                variant="text"
                sx={{ textTransform: "none", mb: 1 }}
              >
                Knowledge Search
              </Button>
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
                Choose text, PDF, Word, PPT, or image
                <input type="file" hidden accept={GUIDE_FILE_ACCEPT} onChange={onFilePick} />
              </Button>
              {guideLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
              {guideReadError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {guideReadError}
                </Alert>
              )}
              {guideRead && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {guideRead.fileName} · {guideRead.text.length} chars
                    {guideRead.truncated ? " (truncated)" : ""}
                  </Typography>
                  <Button variant="contained" onClick={analyzeGuide} disabled={chat.loading}>
                    Analyze with agent
                  </Button>
                  {canAuthorGuides && (
                    <>
                      <TextField
                        select
                        size="small"
                        label="Document type"
                        value={guideEntityClass}
                        onChange={(e) =>
                          setGuideEntityClass(
                            e.target.value as OperatorDocumentEntityClass,
                          )
                        }
                      >
                        {OPERATOR_DOCUMENT_ENTITY_CLASSES.map((id) => (
                          <MenuItem key={id} value={id}>
                            {OPERATOR_DOCUMENT_CLASS_LABELS[id]}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Button variant="text" onClick={saveGuideToLibrary}>
                        Save to guide library
                      </Button>
                    </>
                  )}
                </Stack>
              )}
              <Typography variant="subtitle2" sx={{ mt: 3 }}>
                Saved guides
              </Typography>
              {guidesLoading ? (
                <CircularProgress size={20} />
              ) : (
                savedGuides.map((g) => (
                  <Button key={g.uuid} size="small" onClick={() => openSavedGuide(g)} sx={{ display: "block" }}>
                    {g.name}
                  </Button>
                ))
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                minHeight: 320,
                maxHeight: { xs: 400, md: "min(60vh, 600px)" },
                overflow: "auto",
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Guide content
              </Typography>
              {guideRead ? (
                <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                  {guideRead.text}
                </Typography>
              ) : (
                <Typography color="text.secondary">Upload or open a saved guide to preview.</Typography>
              )}
            </Paper>
            {extractions.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Extraction review
                </Typography>
                {extractions.map((row, idx) => (
                  <Box key={idx} sx={{ mb: 1, borderBottom: 1, borderColor: "divider", pb: 1 }}>
                    {canAuthorGuides && row.matchStatus !== "DUPLICATE" && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!selectedRows[idx]}
                            onChange={(e) =>
                              setSelectedRows((prev) => ({ ...prev, [idx]: e.target.checked }))
                            }
                          />
                        }
                        label="Apply"
                      />
                    )}
                    <Typography variant="body2" fontWeight={600}>
                      {row.proposed.code ?? row.proposed.message ?? "—"} · {row.matchStatus}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.sourceExcerpt}
                    </Typography>
                  </Box>
                ))}
                {canAuthorGuides && (
                  <Button variant="contained" onClick={applyExtractions} disabled={applyLoading}>
                    Apply selected to catalog
                  </Button>
                )}
                {!canAuthorGuides && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Prod support admins and product managers can apply extractions to the catalog.
                  </Alert>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      {tabIndex === 3 && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined">
              <CardActionArea component={RouterLink} to="/support-cases" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Support cases
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View and manage open support cases.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined">
              <CardActionArea component={RouterLink} to="/product-catalog" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Product catalog
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Browse products and subscriptions.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined">
              <CardActionArea component={RouterLink} to="/api-explorer" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600}>
                    API explorer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inspect APIs and endpoints.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
          {canAuthorGuides && (
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined">
                <CardActionArea
                  component={RouterLink}
                  to="/product-manager-dashboard/operational-error-hub"
                  sx={{ height: "100%" }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Manage operational errors
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Curate error codes and remediation in the catalog.
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      <Drawer anchor="right" open={!!selectedError} onClose={() => setSelectedError(null)}>
        <Box sx={{ width: { xs: "100%", sm: "min(480px, 40vw)", md: 520 }, p: 3 }}>
          {selectedError && (
            <>
              <Typography variant="h6">{selectedError.code ?? selectedError.name}</Typography>
              {selectedError.message && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {selectedError.message}
                </Typography>
              )}
              {selectedError.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedError.description}
                </Typography>
              )}
              {selectedError.remediation && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Remediation
                  </Typography>
                  <Typography variant="body2">{selectedError.remediation}</Typography>
                </>
              )}
              {selectedError.errorStrings?.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Match hints
                  </Typography>
                  {selectedError.errorStrings.map((s) => (
                    <Chip key={s} label={s} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </>
              )}
              {selectedError.products?.length > 0 && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Products: {selectedError.products.map((p) => p.name).join(", ")}
                </Typography>
              )}
              {selectedError.apis?.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  APIs: {selectedError.apis.map((a) => a.name).join(", ")}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Drawer>
    </HubPageShell>
  );
};

export default ProductionSupportOperatorHubPage;
